import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  FormControl,
  Select,
  IconButton,
  Divider,
  Button,
  Tooltip,
  Chip,
  ToggleButton,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Fade,
  ToggleButtonGroup,
  useTheme,
} from "@mui/material";
import DomainAddIcon from "@mui/icons-material/DomainAdd";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { AuthContext } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import ComponentLoading from "../App/ComponentLoading";
import "react-datepicker/dist/react-datepicker.css";
import CompanyDialog from "./admin/CompanyDialog";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSuccessDialog } from "../../contexts/SuccessDialogContext";
import { useErrorDialog } from "../../contexts/ErrorDialogContext";
import BusinessIcon from "@mui/icons-material/Business";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { alpha } from "@mui/material/styles";
import AccessDenied from "../Common/AccessDenied";
import { PANEL_COMPANY_MANAGEMENT } from "../../utils/data/controlData";
import useExcelUpload from "../../utils/hooks/useExcelUpload";
import UploadExcelDialog from "../Schedules/utils/UploadExcelEnhanced";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import SortIcon from "@mui/icons-material/Sort";
import CloseIcon from "@mui/icons-material/Close";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import AssessmentIcon from "@mui/icons-material/Assessment";
import FilterListIcon from "@mui/icons-material/FilterList";
import { Payment } from "@mui/icons-material";
import { sanitizeText } from "../../utils/helpers";
import { parseExcelDate } from "../../utils/data/excelData";
import {
  getCompanyListDisplayConfig,
  shouldUseLargeCompanyList,
} from "../../utils/responsiveLayoutSettings";
import {
  buildFinancialYearEndDate,
  getFullYearStartFromEnd,
} from "../../utils/hooks/financialYear";

const MONTH_NAMES_EN = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const formatDateLocaleSafe = (date) => {
  if (!date || isNaN(date.getTime())) return null;
  const day = date.getDate();
  const month = MONTH_NAMES_EN[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

function generateUEN() {
  const year = Math.floor(Math.random() * (2025 - 2000 + 1)) + 2000;
  const serial = Math.floor(Math.random() * 100000)
    .toString()
    .padStart(5, "0");

  const base = `${year}${serial}`;

  // Simple checksum mock (NOT official ACRA algorithm)
  const checksum = String.fromCharCode(
    65 + (base.split("").reduce((sum, d) => sum + Number(d), 0) % 26)
  );

  return `${base}${checksum}`;
}

function getInstructions(subscription) {
  const baseInstructions = [
    ["Col", "Comments"],
    ["Company Name", "Full legal name of the company."],
    ["UEN", "9 digits followed by 1 letter (e.g. 202512345A)."],
    ["Year of Assessment", "Year only (e.g. 2025)."],
    ["Financial Year End", "e.g. 31 December 2024."],
  ];
  if (subscription?.plan_name === "Tax Professional") {
    return [
      ...baseInstructions,
      [
        "Is Restricted?",
        "When enabled, newly created users will not have access to this company unless assigned manually.",
      ],
    ];
  }
  return baseInstructions;
}

const inputSx = {
  borderRadius: 1.5,
  backgroundColor: "background.paper",
  fontSize: "0.8125rem",
  "&:hover": {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "primary.main",
    },
  },
  "&.Mui-focused": {
    boxShadow: (theme) =>
      `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
    borderColor: "primary.main",
  },
};

export const RestrictedChip = () => {
  return (
    <Tooltip
      title='Restricted Company — Only accessible if an admin grants you permission'
      arrow
      placement='top'
    >
      <Box
        component='span'
        sx={{
          ml: 0.5,
          verticalAlign: "middle",
          display: "inline-flex",
          alignItems: "center",
          px: 0.7,
          py: 0.15,
          bgcolor: (theme) => theme.palette.action.hover,
          borderRadius: 1.2,
          border: "1.5px solid",
          boxSizing: "border-box",
          borderColor: (theme) => theme.palette.warning.light,
          color: (theme) => theme.palette.warning.dark,
          height: 24,
          fontSize: 11,
          fontWeight: 600,
          lineHeight: 1,
          maxWidth: "100%",
          boxShadow: "0 1px 2px 0 rgba(180,140,0,0.06)",
          transition: "all 0.2s",
          cursor: "help",
          "&:hover": {
            bgcolor: (theme) => theme.palette.warning.light,
            color: (theme) => theme.palette.warning.contrastText,
            borderColor: (theme) => theme.palette.warning.main,
            boxShadow: "0 2px 8px 0 rgba(180,140,0,0.12)",
          },
        }}
      >
        <LockOpenIcon
          sx={{
            fontSize: 15,
            mr: 0.5,
            color: "inherit",
          }}
        />
        Restricted
      </Box>
    </Tooltip>
  );
};

const CompanyList = () => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [editopen, setEditOpen] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [uen, setUEN] = useState("");
  const [financialYearEnd, setFinancialYearEnd] = useState(null);
  const [isFullYear, setIsFullYear] = useState(true);
  const [isRestricted, setIsRestricted] = useState(false);
  const [financialYearStart, setFinancialYearStart] = useState(null);
  const [yearOfAssessment, setYearOfAssessment] = useState("");
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [companyToEdit, setCompanyToEdit] = useState(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null); // Anchor element for menu
  const [searchTerm, setSearchTerm] = useState("");
  const [sortType, setSortType] = useState("financialYearEnd"); // default sorting by financial year end
  const [yearFilter, setYearFilter] = useState("all");
  const [companyType, setCompanyType] = useState("common");
  const [batchProcessing, setBatchProcessing] = useState(false);

  const [displayMode, setDisplayMode] = useState(() => {
    try {
      const saved = localStorage.getItem("companyList.displayMode");
      return saved === "list" || saved === "card" ? saved : "card";
    } catch {
      return "card";
    }
  });
  const [useLargeLayout, setUseLargeLayout] = useState(() =>
    shouldUseLargeCompanyList()
  );
  const displayConfig = useMemo(
    () => getCompanyListDisplayConfig(useLargeLayout),
    [useLargeLayout]
  );

  useEffect(() => {
    try {
      localStorage.setItem("companyList.displayMode", displayMode);
    } catch {}
  }, [displayMode]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const updateLayoutMode = () => {
      setUseLargeLayout(shouldUseLargeCompanyList());
    };

    updateLayoutMode();
    window.addEventListener("resize", updateLayoutMode);
    window.visualViewport?.addEventListener("resize", updateLayoutMode);

    return () => {
      window.removeEventListener("resize", updateLayoutMode);
      window.visualViewport?.removeEventListener("resize", updateLayoutMode);
    };
  }, []);

  const {
    createCompany,
    getCompanyList,
    tempDeleteCompany,
    updateCompany,
    global_authState,
    checkAccess,
  } = useContext(AuthContext);
  const navigate = useNavigate();
  const handleSuccess = useSuccessDialog();
  const handleError = useErrorDialog();

  const { subscription, user_role } = global_authState;

  // Access control checks
  const canViewCompanies = checkAccess("view", PANEL_COMPANY_MANAGEMENT);
  const canManageCompanies = checkAccess("edit", PANEL_COMPANY_MANAGEMENT);
  const canDeleteCompanies = checkAccess("delete", PANEL_COMPANY_MANAGEMENT);

  const {
    openDialog: uploadDialogOpen,
    selectedFile,
    uploading,
    rowCount: validRows,
    errorFileUrl,
    handleOpenDialog: openUploadDialog,
    handleCloseDialog: closeUploadDialog,
    handleCloseAlert: closeUploadAlert,
    handleDownloadTemplate: downloadCompanyTemplate,
    handleFileChange: handleUploadFileChange,
    handleResetFile: handleUploadReset,
    handleUpload: triggerUpload,
    getTemplateHeadersString,
  } = useExcelUpload({
    fileName: "company_batch_upload_template.xlsx",
    instructions: getInstructions(subscription),
    sampleData: [
      {
        "Company Name": "Sample Company Pte Ltd",
        UEN: generateUEN(),
        "Year of Assessment": 2025,
        "Financial Year End": "31 December 2024",
        ...(subscription?.plan_name === "Tax Professional"
          ? { "Is Restricted?": false }
          : {}),
      },
      {
        "Company Name": "Another Ltd",
        UEN: generateUEN(),
        "Year of Assessment": 2025,
        "Financial Year End": "30 June 2024",
        ...(subscription?.plan_name === "Tax Professional"
          ? { "Is Restricted?": false }
          : {}),
      },
    ],
    validationSchema: [
      { type: "text", required: true },
      { type: "text", required: true },
      { type: "number", required: true },
      { type: "date", required: true },
      ...(subscription?.plan_name === "Tax Professional"
        ? [{ type: "boolean", required: false }]
        : []),
    ],
    onUpload: async (excelData, rowCount) => {
      setBatchProcessing(true);
      const failed = [];
      let created = 0;
      try {
        for (const row of excelData) {
          const [name, uenVal, yearOfAssessmentVal, fyEnd] = row;
          const nameStr = sanitizeText(name);
          const uenStr = sanitizeText(uenVal);
          try {
            const fyEndDate = parseExcelDate(fyEnd);
            if (!fyEndDate) {
              throw new Error("Financial Year End is not a valid date.");
            }
            const fyStartDate = new Date(
              yearOfAssessmentVal - 2,
              fyEndDate.getMonth() + 1,
              1
            );
            const fyStart = formatDateLocaleSafe(fyStartDate);
            const normalizedFyEnd = formatDateLocaleSafe(fyEndDate);
            const fyStartStr =
              fyStart != null && String(fyStart).trim() !== ""
                ? String(fyStart).trim()
                : normalizedFyEnd;

            let isRestricted = false;

            if (subscription?.plan_name === "Tax Professional") {
              isRestricted = row[4];
            }
            await createCompany({
              name: nameStr,
              uen: uenStr,
              yearOfAssessment: Number(yearOfAssessmentVal) || "",
              financialYearEnd: normalizedFyEnd,
              financialYearStart: fyStartStr,
              isRestricted: isRestricted,
              companyType: "common",
            });
            created += 1;
          } catch (err) {
            const reason =
              err?.response?.data?.message || err?.message || "Unknown error";
            failed.push({ name: nameStr || "(no name)", uen: uenStr, reason });
          }
        }
        const updated = await getCompanyList();
        setCompanies(updated);
        if (failed.length === 0) {
          handleSuccess(
            `Successfully created ${created} compan${created === 1 ? "y" : "ies"}.`
          );
        } else {
          const failedSummary = failed
            .map((f) => `${f.name} (${f.uen}): ${f.reason}`)
            .join("\n");
          const summary =
            created > 0
              ? `Created ${created} compan${created === 1 ? "y" : "ies"}. ${failed.length} failed:\n${failedSummary}`
              : `All ${failed.length} companies failed:\n${failedSummary}`;
          handleError(summary);
        }
      } finally {
        setBatchProcessing(false);
      }
    },
  });

  useEffect(() => {
    const fetchCompanies = async () => {
      setLoading(true);
      try {
        const companyList = await getCompanyList();
        setCompanies(companyList);
      } catch (error) {
        handleError(error);
      }
      setLoading(false);
    };

    fetchCompanies();
  }, [global_authState.subscription]);

  const sortedAndFilteredCompanies = companies
    .filter((company) => {
      const isActive = company.status === "active";
      const matchesSearch =
        company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.uen.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesYear =
        yearFilter === "all" ||
        (company.yearOfAssessment &&
          company.yearOfAssessment.toString() === yearFilter);

      return isActive && matchesSearch && matchesYear;
    })
    .sort((a, b) => {
      if (sortType === "financialYearEnd") {
        const dateA = new Date(a.financialYearEnd);
        const dateB = new Date(b.financialYearEnd);

        // Sort by month first, then by year
        if (dateA.getMonth() !== dateB.getMonth()) {
          return dateA.getMonth() - dateB.getMonth();
        } else {
          return dateA.getFullYear() - dateB.getFullYear();
        }
      }
      if (sortType === "alphabetical") {
        return a.name.localeCompare(b.name);
      }
      return 0;
    });

  const handleOptionsClick = (event, company) => {
    setCompanyToEdit(company);
    setCompanyName(company.name);
    setUEN(company.uen);
    setCompanyType(company.companyType);
    setFinancialYearEnd(company.financialYearEnd);
    setIsFullYear(company.isFullYear);
    setIsRestricted(company.isRestricted);
    setFinancialYearStart(company.financialYearStart);
    setYearOfAssessment(company.yearOfAssessment);
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // Reset form fields to default values
  const resetFormFields = () => {
    setCompanyName("");
    setUEN("");
    setFinancialYearEnd(null);
    setIsFullYear(true);
    setIsRestricted(false);
    setFinancialYearStart(null);
    setYearOfAssessment("");
    setCompanyType("common");
  };

  // Handle dialog close with proper cleanup
  const handleDialogClose = (dialogType) => {
    resetFormFields();
    if (dialogType === "add") {
      setOpen(false);
    } else if (dialogType === "edit") {
      setEditOpen(false);
      setCompanyToEdit(null);
    } else if (dialogType === "delete") {
      setDeleteDialogOpen(false);
      setCompanyToDelete(null);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleCloseMenu();
  };

  const handleDeleteConfirm = async () => {
    if (!companyToDelete) {
      handleError("No company selected for deletion.");
      return;
    }

    setDialogLoading(true);
    try {
      await tempDeleteCompany({ c_id: companyToDelete.c_id });
      handleSuccess(`Successfully deleted company "${companyToDelete.name}".`);

      // Refresh the company list
      const updatedCompanies = await getCompanyList();
      setCompanies(updatedCompanies);

      // Close dialog and reset state
      setDeleteDialogOpen(false);
      setCompanyToDelete(null);
    } catch (error) {
      handleError(
        error.response?.data?.message ||
          "Failed to delete company. Please try again."
      );
    } finally {
      setDialogLoading(false);
    }
  };

  const handleEditClick = () => {
    setEditOpen(true);
    handleCloseMenu();
  };

  const handleEditConfirm = async () => {
    // Validate required fields
    if (!companyName.trim()) {
      handleError("Company name is required.");
      return;
    }
    if (!uen.trim()) {
      handleError("UEN is required.");
      return;
    }
    if (!yearOfAssessment) {
      handleError("Year of Assessment is required.");
      return;
    }
    if (!financialYearEnd) {
      handleError("Financial Year End is required.");
      return;
    }

    setDialogLoading(true);
    try {
      let fyEnd = financialYearEnd;
      let fyStart = financialYearStart;

      if (isFullYear && financialYearEnd) {
        const selectedYearEnd = new Date(financialYearEnd);
        if (!Number.isNaN(selectedYearEnd.getTime())) {
          const fyEndDate = buildFinancialYearEndDate(
            yearOfAssessment - 1,
            selectedYearEnd.getMonth(),
            selectedYearEnd.getDate()
          );
          const fyStartDate = getFullYearStartFromEnd(fyEndDate);
          fyEnd = formatDateLocaleSafe(fyEndDate) || financialYearEnd;
          fyStart = formatDateLocaleSafe(fyStartDate) || financialYearStart;
        }
      }

      const updateData = {
        c_id: companyToEdit.c_id,
        uen: companyToEdit.uen,
        name: companyName.trim(),
        newUen: uen.trim(),
        financialYearEnd: fyEnd,
        isFullYear: isFullYear,
        isRestricted:
          subscription?.plan_name === "Tax Professional" ? isRestricted : false,
        financialYearStart: fyStart,
        yearOfAssessment: yearOfAssessment,
        companyType: companyType,
      };

      await updateCompany(updateData);
      handleSuccess(`Successfully updated company "${companyName.trim()}".`);

      // Refresh the company list
      const updatedCompanies = await getCompanyList();
      setCompanies(updatedCompanies);

      // Reset form and close dialog
      resetFormFields();
      setEditOpen(false);
      setCompanyToEdit(null);
    } catch (error) {
      if (error.response?.status === 409) {
        handleError(
          "This UEN is already associated with another company in your company list."
        );
      } else {
        handleError(
          error.response?.data?.message ||
            "Failed to update company. Please try again."
        );
      }
    } finally {
      setDialogLoading(false);
    }
  };

  const handleSortChange = (event) => {
    setSortType(event.target.value);
  };

  const handleAddCompany = async () => {
    // Validate required fields
    if (!companyName.trim()) {
      handleError("Company name is required.");
      return;
    }
    if (!uen.trim()) {
      handleError("UEN is required.");
      return;
    }
    if (!yearOfAssessment) {
      handleError("Year of Assessment is required.");
      return;
    }
    if (!financialYearEnd) {
      handleError("Financial Year End is required.");
      return;
    }

    const selectedYearEnd = new Date(financialYearEnd);
    if (isNaN(selectedYearEnd.getTime())) {
      handleError("Financial Year End is not a valid date.");
      return;
    }

    let fyStart = "";
    let fyEnd = "";

    // Full year logic
    if (isFullYear) {
      const fyEndDate = buildFinancialYearEndDate(
        yearOfAssessment - 1,
        selectedYearEnd.getMonth(),
        selectedYearEnd.getDate()
      );
      const fyStartDate = getFullYearStartFromEnd(fyEndDate);

      fyStart = formatDateLocaleSafe(fyStartDate);
      fyEnd = formatDateLocaleSafe(fyEndDate);
      if (fyStart == null || fyEnd == null) {
        handleError("Could not compute financial year dates.");
        return;
      }
    } else {
      // Use user-provided values
      fyStart = financialYearStart;
      fyEnd = financialYearEnd;
    }

    setDialogLoading(true);
    try {
      const companyData = {
        name: companyName.trim(),
        uen: uen.trim(),
        financialYearEnd: fyEnd,
        financialYearStart: fyStart,
        yearOfAssessment: yearOfAssessment,
        companyType: companyType,
        isRestricted:
          subscription?.plan_name === "Tax Professional" ? isRestricted : false,
      };

      await createCompany(companyData);
      handleSuccess(`Successfully created company "${companyName.trim()}".`);

      // Refresh the company list
      const updatedCompanies = await getCompanyList();
      setCompanies(updatedCompanies);

      // Reset form and close dialog
      resetFormFields();
      setOpen(false);
    } catch (error) {
      if (error.response?.status === 409) {
        handleError(
          "This UEN is already associated with a company in your company list."
        );
      } else {
        handleError(
          error.response?.data?.message ||
            "Failed to create company. Please try again."
        );
      }
    } finally {
      setDialogLoading(false);
    }
  };

  // Get unique years for filter
  const availableYears = [
    ...new Set(companies.map((company) => company.yearOfAssessment)),
  ].sort((a, b) => b - a);

  const hasFilters = Boolean(searchTerm) || yearFilter !== "all";
  const activeCompanyCount = companies.filter(
    (company) => company.status === "active"
  ).length;
  const clearFilters = () => {
    setSearchTerm("");
    setYearFilter("all");
  };

  if (!canViewCompanies) {
    return (
      <AccessDenied
        title='Access Denied'
        message="You don't have permission to view companies. Please contact your administrator if you believe this is an error."
      />
    );
  }

  if (loading) {
    return <ComponentLoading />;
  }

  if (
    !loading &&
    global_authState.subscription &&
    (global_authState.subscription.status === "cancelled" ||
      global_authState.subscription.status === "expired")
  ) {
    return (
      <AccessDenied
        title='Account Activation Required'
        message='Your subscription is no longer active. To continue accessing companies, please update your billing.'
        customAction={
          <Button
            onClick={() => (window.location.href = "/settings?tab=billings")}
            variant='contained'
            startIcon={<Payment />}
            sx={{
              fontWeight: 700,
              bgcolor: "warning.main",
              "&:hover": {
                bgcolor: "warning.dark",
              },
            }}
          >
            Go to Billing
          </Button>
        }
      />
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        width: "100%",
        minWidth: 0,
        minHeight: "100%",
        pb: { xs: 3, sm: 4 },
        bgcolor: "grey.50",
        "& > *": { flexShrink: 0 },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          width: "100%",
          bgcolor: "background.paper",
          px: displayConfig.header.px,
          py: displayConfig.header.py,
          mb: hasFilters ? 0 : displayConfig.header.mb,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.025)",
          borderBottom: "1px solid",
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.25),
          position: "relative",
          overflow: "visible",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: -1,
            left: 0,
            right: 0,
            height: "2px",
            background:
              "linear-gradient(90deg, transparent, #603F97 30%, #D31D83 70%, transparent)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", lg: "center" },
            gap: { xs: 1.5, sm: 2 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              minWidth: 0,
              flexShrink: 0,
              py: { xs: 0.25, sm: 0 },
            }}
          >
            <Box
              sx={{
                width: displayConfig.header.iconSize,
                height: displayConfig.header.iconSize,
                borderRadius: "12px",
                background: "linear-gradient(135deg, #603F97 0%, #D31D83 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                boxShadow: "0 4px 10px rgba(96, 63, 151, 0.25)",
              }}
            >
              <BusinessIcon sx={{ color: "white", fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component='h1'
                variant='h5'
                sx={{
                  fontWeight: 700,
                  background: "linear-gradient(45deg, #603F97 30%, #D31D83 90%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: displayConfig.header.titleFontSize,
                  lineHeight: 1.2,
                }}
              >
                {user_role === "admin"
                  ? "Company Management"
                  : "Company Directory"}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" }, // 在 md 断点才变行，给手机更多垂直空间
              gap: displayConfig.header.controlsGap,
              alignItems: { xs: "stretch", md: "center" },
              width: "100%",
              minWidth: 0,
              justifyContent: "flex-end",
            }}
          >
            {/* 左侧：搜索 + 操作 (在移动端占据全宽，桌面端自适应) */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                minWidth: 0, // 防止 flex 子项溢出
                alignItems: "flex-end",
              }}
            >
              <TextField
                variant='outlined'
                size='small'
                placeholder='Search companies...'
                inputProps={{ "aria-label": "Search by company name or UEN" }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  // 移除自定义 py，依赖 size="small" 保证高度统一
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon sx={{ color: "grey.500", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position='end' sx={{ cursor: "pointer" }}>
                      <Tooltip title='Clear search' arrow>
                        <IconButton
                          size='small'
                          aria-label='Clear search'
                          onClick={() => setSearchTerm("")}
                        >
                          <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  maxWidth: displayConfig.header.searchMaxWidth,
                  ...inputSx,
                  "& .MuiOutlinedInput-root": {
                    ...inputSx,
                    pr: 1,
                  },
                }}
              />

              {canManageCompanies && (
                <Tooltip title='Import multiple companies at once' arrow>
                  <Button
                    variant='outlined'
                    startIcon={<UploadFileIcon />}
                    onClick={openUploadDialog}
                    sx={{
                      flexShrink: 0,
                      display: { xs: "none", sm: "flex" },
                      textTransform: "none",
                      borderRadius: 1.5,
                      borderColor: "primary.main",
                      color: "primary.main",
                      fontWeight: 600,
                      px: 1.5,
                      "&:hover": {
                        borderColor: "primary.dark",
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.04
                        ),
                      },
                    }}
                  >
                    Import
                  </Button>
                </Tooltip>
              )}
            </Box>

            {/* 右侧：过滤器 + 视图切换 (作为一个整体组，避免内部换行) */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap", // 允许整体换行，但内部元素尽量保持一行
                alignItems: "center",
                minWidth: 0,
                maxWidth: "100%",
                flexShrink: 0,
                justifyContent: { xs: "space-between", md: "flex-end" },
              }}
            >
              <FormControl
                size='small'
                sx={{ minWidth: 110 }}
              >
                <InputLabel id='company-sort-label' sx={{ fontSize: "0.8125rem" }}>
                  Sort
                </InputLabel>
                <Select
                  labelId='company-sort-label'
                  value={sortType}
                  onChange={handleSortChange}
                  label='Sort'
                  sx={inputSx}
                >
                  <MenuItem value='financialYearEnd'>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                      <AssessmentIcon sx={{ fontSize: 18 }} /> FY End
                    </Box>
                  </MenuItem>
                  <MenuItem value='alphabetical'>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                      <SortIcon sx={{ fontSize: 18 }} /> Name (A-Z)
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              <FormControl
                size='small'
                sx={{ minWidth: 90 }}
              >
                <InputLabel id='company-year-label' sx={{ fontSize: "0.8125rem" }}>
                  Filter by year
                </InputLabel>
                <Select
                  labelId='company-year-label'
                  value={yearFilter}
                  onChange={(e) => setYearFilter(e.target.value)}
                  label='Filter by year'
                  sx={inputSx}
                >
                  <MenuItem value='all'>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                      <FilterListIcon sx={{ fontSize: 18 }} /> All
                    </Box>
                  </MenuItem>
                  {availableYears.map((year) => (
                    <MenuItem key={year} value={year.toString()}>
                      YA {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* 使用 ToggleButtonGroup 替代两个独立的 ToggleButton */}
              <ToggleButtonGroup
                value={displayMode}
                exclusive
                onChange={(e, newVal) => newVal && setDisplayMode(newVal)}
                size='small'
                aria-label='Company display mode'
                sx={{
                  "& .MuiToggleButton-root": {
                    borderRadius: 1.25,
                    p: 0.625,
                    minWidth: "auto",
                    border: "1px solid",
                    borderColor: "grey.200",
                    "&.Mui-selected": {
                      backgroundColor: (theme) =>
                        alpha(theme.palette.primary.main, 0.12),
                      color: "primary.main",
                      borderColor: "transparent",
                      boxShadow: (theme) =>
                        `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
                    },
                    "&:hover": {
                      backgroundColor: (theme) =>
                        alpha(theme.palette.grey[200], 0.6),
                    },
                  },
                }}
              >
                <ToggleButton value='card' aria-label='Card view'>
                  <Tooltip title='Card view' arrow>
                    <GridViewIcon sx={{ fontSize: 18 }} />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value='list' aria-label='List view'>
                  <Tooltip title='List view' arrow>
                    <ViewListIcon sx={{ fontSize: 18 }} />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        </Box>
      </Box>

      {hasFilters && (
        <Box
          sx={{
            px: displayConfig.header.px,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography variant='body2' color='text.secondary' role='status'>
            {sortedAndFilteredCompanies.length} of {activeCompanyCount} companies
          </Typography>
          <Button
            size='small'
            onClick={clearFilters}
            sx={{ textTransform: "none" }}
          >
            Clear filters
          </Button>
        </Box>
      )}

      {sortedAndFilteredCompanies.length === 0 ? (
        <Paper
          variant='outlined'
          sx={{
            mx: displayConfig.header.px,
            p: { xs: 3, sm: 6 },
            borderRadius: 3,
            textAlign: "center",
          }}
        >
          <BusinessIcon sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
          <Typography variant='h6' fontWeight={600}>
            {hasFilters ? "No companies found" : "No companies yet"}
          </Typography>
          <Typography
            color='text.secondary'
            variant='body2'
            sx={{ mt: 1, mb: 2.5 }}
          >
            {hasFilters
              ? "Try another company name, UEN, or year of assessment."
              : canManageCompanies
                ? "Add your first company to start preparing its tax schedules."
                : "Companies will appear here when you are granted access."}
          </Typography>
          {hasFilters ? (
            <Button
              variant='outlined'
              onClick={clearFilters}
              sx={{ textTransform: "none" }}
            >
              Clear filters
            </Button>
          ) : (
            canManageCompanies && (
              <Button
                variant='contained'
                startIcon={<DomainAddIcon />}
                onClick={() => setOpen(true)}
                sx={{ textTransform: "none", boxShadow: "none" }}
              >
                Add company
              </Button>
            )
          )}
        </Paper>
      ) : displayMode === "card" ? (
        <Fade in key='card' timeout={320}>
          <Box sx={{ width: "100%", px: displayConfig.header.px }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fill, minmax(min(100%, 240px), 1fr))",
                gap: displayConfig.grid.spacing,
              }}
            >
              {canManageCompanies && (
                <Box sx={{ minWidth: 0 }}>
                  <Card
                    sx={{
                      height: displayConfig.addCard.height,
                      borderRadius: 2,
                      background:
                        "linear-gradient(135deg, #603F97 0%, #D31D83 100%)",
                      transition: "all 0.3s ease-in-out",
                      position: "relative",
                      overflow: "hidden",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 12px 30px rgba(96, 63, 151, 0.2)",
                        "& .add-icon": {
                          transform: "scale(1.1) rotate(5deg)",
                        },
                        "& .add-text": {
                          transform: "translateY(-2px)",
                        },
                      },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background:
                          "linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)",
                        zIndex: 1,
                      },
                    }}
                    onClick={() => setOpen(true)}
                  >
                    <CardActionArea
                      sx={{ height: "100%", position: "relative", zIndex: 2 }}
                    >
                      <CardContent
                        sx={{
                          height: "100%",
                          p: displayConfig.addCard.contentPadding,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                        }}
                      >
                        <Box
                          className='add-icon'
                          sx={{
                            backgroundColor: "rgba(255, 255, 255, 0.15)",
                            borderRadius: "50%",
                            p: displayConfig.addCard.iconPadding,
                            mb: displayConfig.card.topGap,
                            transition: "all 0.3s ease-in-out",
                            backdropFilter: "blur(4px)",
                          }}
                        >
                          <DomainAddIcon
                            sx={{ fontSize: displayConfig.addCard.iconSize }}
                          />
                        </Box>
                        <Typography
                          className='add-text'
                          variant={displayConfig.addCard.titleVariant}
                          sx={{
                            fontWeight: 600,
                            lineHeight: 1.25,
                            textAlign: "center",
                            transition: "all 0.3s ease-in-out",
                          }}
                        >
                          Add New Company
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Box>
              )}

              {sortedAndFilteredCompanies.map((company) => (
                <Box sx={{ minWidth: 0 }} key={company.c_id || company.uen}>
                  <Card
                    sx={{
                      height: displayConfig.card.height,
                      borderRadius: 3, // 稍微增加圆角，更现代
                      bgcolor: "background.paper",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.025)",
                      border: "1px solid",
                      borderColor: "divider",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)", // 更平滑的贝塞尔曲线
                      cursor: "pointer",
                      position: "relative",
                      overflow: "hidden", // 防止内容溢出
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: (theme) =>
                          `0 12px 24px -8px ${alpha(theme.palette.primary.main, 0.25)}`, // 更高级的阴影
                        borderColor: "primary.main",
                        "& .company-options": {
                          opacity: 1,
                          transform: "translateY(0)",
                        },
                        "& .company-icon": {
                          bgcolor: (theme) =>
                            alpha(theme.palette.primary.main, 0.15),
                          transform: "scale(1.1)",
                        },
                      },
                    }}
                    onClick={() => navigate(`/company`, { state: { company } })}
                  >
                    <CardContent
                      sx={{
                        height: "100%",
                        p: displayConfig.card.contentPadding,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                        }}
                      >
                        {/* 头部区域：图标与操作按钮 */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            mb: displayConfig.card.topGap,
                          }}
                        >
                          <Box
                            className='company-icon'
                            sx={{
                              width: displayConfig.card.iconSize,
                              height: displayConfig.card.iconSize,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: displayConfig.card.iconRadius,
                              bgcolor: (theme) =>
                                alpha(theme.palette.primary.main, 0.08),
                              color: "primary.main",
                              transition: "all 0.3s ease",
                            }}
                          >
                            <BusinessIcon />
                          </Box>

                          {canManageCompanies && (
                            <IconButton
                              className='company-options'
                              size='small'
                              aria-label={`Options for ${company.name}`}
                              sx={{
                                backgroundColor: "background.paper",
                                border: "1px solid",
                                borderColor: "divider",
                                opacity: 1,
                                flexShrink: 0,
                                height: 34,
                                width: 34,
                                transition: "all 0.2s ease-in-out",
                                "&:hover": {
                                  backgroundColor: "primary.main",
                                  color: "white",
                                  borderColor: "primary.main",
                                },
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOptionsClick(e, company);
                              }}
                            >
                              <MoreVertIcon fontSize='small' />
                            </IconButton>
                          )}
                        </Box>

                        {/* 公司信息区域 */}
                        <Box
                          sx={{
                            flexGrow: 1,
                            minHeight: 0,
                            minWidth: 0,
                            overflow: "hidden",
                          }}
                        >
                          {company.name.length > 18 ? (
                            <Tooltip title={company.name} arrow>
                              <Typography
                                variant='h6'
                                fontWeight={700}
                                color={"text.primary"}
                                sx={{
                                  mb: 1,
                                  lineHeight: 1.25,
                                  display: "-webkit-box",
                                  WebkitLineClamp:
                                    displayConfig.card.nameLineClamp,
                                  minWidth: 0,
                                  overflowWrap: "anywhere",
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  cursor: "pointer",
                                }}
                              >
                                {company.name}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography
                              variant='h6'
                              fontWeight={700}
                              color={"text.primary"}
                              sx={{
                                mb: 1,
                                lineHeight: 1.25,
                                display: "-webkit-box",
                                WebkitLineClamp:
                                  displayConfig.card.nameLineClamp,
                                minWidth: 0,
                                overflowWrap: "anywhere",
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {company.name}
                            </Typography>
                          )}

                          <Box
                            sx={{
                              alignItems: "center",
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.75,
                              maxHeight: 64,
                              minWidth: 0,
                              overflow: "hidden",
                            }}
                          >
                            <Chip
                              icon={
                                <BusinessCenterIcon sx={{ fontSize: 14 }} />
                              }
                              label={company.uen}
                              size='small'
                              variant='filled' // 使用 filled 增加视觉重量
                              sx={{
                                height: 24,
                                fontSize: "0.75rem",
                                fontWeight: 500,
                                alignSelf: "flex-start",
                                maxWidth: "100%",
                                bgcolor: (theme) =>
                                  alpha(theme.palette.primary.main, 0.1),
                                color: "primary.main",
                                borderRadius: 1,
                                "& .MuiChip-icon": {
                                  color: "inherit",
                                },
                                "& .MuiChip-label": {
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                },
                              }}
                            />
                            {company.isRestricted &&
                              subscription?.plan_name ===
                                "Tax Professional" && (
                                <Box sx={{ minWidth: 0 }}>
                                  <RestrictedChip />
                                </Box>
                              )}
                          </Box>
                        </Box>

                        {/* 底部状态区域 */}
                        <Box
                          sx={{
                            mt: "auto",
                            pt: displayConfig.card.footerPaddingTop,
                            borderTop: "1px dashed", // 使用虚线区分层级
                            borderColor: "divider",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            justifyContent: "space-between",
                            minWidth: 0,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              minWidth: 0,
                            }}
                          >
                            <CalendarTodayIcon
                              fontSize='inherit'
                              sx={{ color: "text.disabled" }}
                            />
                            <Typography
                              variant='caption'
                              fontWeight={600}
                              color='text.secondary'
                              sx={{
                                lineHeight: 1.2,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              YA {company.yearOfAssessment}
                            </Typography>
                          </Box>

                          <Typography
                            variant='caption'
                            sx={{
                              color: "text.disabled",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              fontWeight: 500,
                              lineHeight: 1.2,
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {company.financialYearEnd
                              ? new Date(
                                  company.financialYearEnd
                                ).toLocaleString("default", {
                                  month: "short",
                                  day: "numeric",
                                })
                              : "N/A"}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
          </Box>
        </Fade>
      ) : (
        <Fade in key='list' timeout={320}>
          <Box sx={{ width: "100%" }}>
            <Box
              sx={{ px: displayConfig.header.px, width: "100%", minWidth: 0 }}
            >
              {canManageCompanies && (
                <Card
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    background:
                      "linear-gradient(135deg, #603F97 0%, #D31D83 100%)",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      boxShadow: "0 12px 30px rgba(96, 63, 151, 0.2)",
                      "& .add-icon": { transform: "scale(1.1) rotate(5deg)" },
                      "& .add-text": { transform: "translateY(-2px)" },
                    },
                  }}
                  onClick={() => setOpen(true)}
                >
                  <CardActionArea>
                    <CardContent
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        color: "white",
                        py: 1.5,
                        "&:last-child": { pb: 1.5 },
                      }}
                    >
                      <Box
                        className='add-icon'
                        sx={{
                          backgroundColor: "rgba(255, 255, 255, 0.15)",
                          borderRadius: "50%",
                          p: 1.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.3s ease-in-out",
                        }}
                      >
                        <DomainAddIcon sx={{ fontSize: 28 }} />
                      </Box>
                      <Typography
                        className='add-text'
                        variant='subtitle1'
                        fontWeight={600}
                      >
                        Add New Company
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              )}

              <TableContainer
                component={Paper}
                variant='outlined'
                sx={{
                  borderRadius: 3,
                  overflowX: "auto",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.025)",
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                }}
              >
                <Table size='medium' aria-label='Companies'>
                  <TableHead>
                    <TableRow
                      sx={{
                        "& .MuiTableCell-head": {
                          bgcolor: (theme) =>
                            alpha(theme.palette.primary.main, 0.04),
                          color: "text.secondary",
                          textTransform: "uppercase", // 表头大写，更显专业
                          letterSpacing: "0.5px",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          py: 2,
                        },
                      }}
                    >
                      <TableCell sx={{ minWidth: 280 }}>Company</TableCell>
                      <TableCell sx={{ minWidth: 160 }}>UEN</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>YA</TableCell>
                      <TableCell sx={{ minWidth: 160 }}>FY End</TableCell>
                      {canManageCompanies && (
                        <TableCell
                          align='right'
                          sx={{ minWidth: 120, width: 120 }}
                        >
                          Actions
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedAndFilteredCompanies.map((company) => (
                      <TableRow
                        key={company.c_id || company.uen} // 建议使用唯一ID作为key
                        hover
                        sx={{
                          cursor: "pointer",
                          // 受限状态显示灰色背景，正常状态保持白色
                          bgcolor: "background.paper",
                          transition: "background-color 0.2s ease",
                          "&:hover": {
                            bgcolor: (theme) =>
                              alpha(theme.palette.primary.main, 0.04),
                          },
                          // 移除最后一行的底部边框，配合 TableContainer 的边框
                          "&:last-child td": { borderBottom: 0 },
                        }}
                        onClick={() =>
                          navigate(`/company`, { state: { company } })
                        }
                      >
                        {/* Company Name 列 */}
                        <TableCell sx={{ py: 1.5, verticalAlign: "middle" }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: 2,
                                bgcolor: (theme) =>
                                  alpha(theme.palette.primary.main, 0.08),
                                color: "primary.main",
                                transition: "all 0.2s",
                              }}
                            >
                              <BusinessIcon fontSize='small' />
                            </Box>
                            <Box sx={{ overflow: "hidden" }}>
                              <Typography
                                variant='body2'
                                fontWeight={600}
                                color={"text.primary"}
                                noWrap
                                sx={{ display: "block" }}
                              >
                                {company.name}
                                {company.isRestricted &&
                                  subscription?.plan_name ===
                                    "Tax Professional" && <RestrictedChip />}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        {/* UEN 列 */}
                        <TableCell sx={{ py: 1.5, verticalAlign: "middle" }}>
                          <Chip
                            label={company.uen}
                            size='small'
                            variant='outlined'
                            sx={{
                              fontWeight: 500,
                              bgcolor: (theme) =>
                                alpha(theme.palette.primary.main, 0.06),
                              color: "text.primary",
                              borderColor: "divider",
                              height: 24,
                              fontSize: "0.75rem",
                            }}
                          />
                        </TableCell>

                        {/* YA 列 */}
                        <TableCell sx={{ py: 1.5, verticalAlign: "middle" }}>
                          <Typography
                            variant='body2'
                            color='text.secondary'
                            fontWeight={500}
                          >
                            YA {company.yearOfAssessment}
                          </Typography>
                        </TableCell>

                        {/* FY End 列 */}
                        <TableCell sx={{ py: 1.5, verticalAlign: "middle" }}>
                          <Typography variant='body2' color='text.secondary'>
                            {company.financialYearEnd
                              ? new Date(
                                  company.financialYearEnd
                                ).toLocaleString("default", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                })
                              : "N/A"}
                          </Typography>
                        </TableCell>

                        {/* Actions 列 */}
                        {canManageCompanies && (
                          <TableCell
                            align='right'
                            onClick={(e) => e.stopPropagation()}
                            sx={{ py: 1.5, verticalAlign: "middle" }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.5,
                                justifyContent: "flex-end",
                              }}
                            >
                              <Tooltip title='Edit' arrow>
                                <IconButton
                                  size='small'
                                  onClick={() => {
                                    // 提取出的编辑逻辑，保持JSX整洁
                                    setCompanyToEdit(company);
                                    setCompanyName(company.name);
                                    setUEN(company.uen);
                                    setCompanyType(company.companyType);
                                    setFinancialYearEnd(
                                      company.financialYearEnd
                                    );
                                    setIsFullYear(company.isFullYear ?? true);
                                    setIsRestricted(
                                      subscription?.plan_name ===
                                        "Tax Professional"
                                        ? (company.isRestricted ?? false)
                                        : false
                                    );
                                    setFinancialYearStart(
                                      company.financialYearStart
                                    );
                                    setYearOfAssessment(
                                      company.yearOfAssessment
                                    );
                                    setEditOpen(true);
                                  }}
                                  sx={{
                                    color: "text.secondary",
                                    "&:hover": {
                                      bgcolor: (theme) =>
                                        alpha(theme.palette.primary.main, 0.08),
                                      color: "primary.main",
                                    },
                                  }}
                                >
                                  <EditIcon
                                    fontSize='small'
                                    sx={{ color: "primary.main" }}
                                  />
                                </IconButton>
                              </Tooltip>
                              {canDeleteCompanies && (
                                <Tooltip title='Delete' arrow>
                                  <IconButton
                                    size='small'
                                    onClick={() => {
                                      setCompanyToDelete(company);
                                      setDeleteDialogOpen(true);
                                    }}
                                    sx={{
                                      color: "text.secondary",
                                      "&:hover": {
                                        bgcolor: (theme) =>
                                          alpha(theme.palette.error.main, 0.08),
                                        color: "error.main",
                                      },
                                    }}
                                  >
                                    <DeleteIcon
                                      fontSize='small'
                                      sx={{ color: "error.main" }}
                                    />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </Fade>
      )}

      {/* Enhanced Menu - Only show if user has manage access */}
      {canManageCompanies && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          // 核心对齐优化：让菜单的左上角对齐按钮的左下角
          transformOrigin={{ horizontal: "left", vertical: "top" }}
          anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
          // 增加一点展开的过渡动画
          TransitionProps={{ timeout: 200 }}
          PaperProps={{
            elevation: 0, // 去掉默认 elevation，使用自定义 boxShadow
            sx: {
              mt: 1, // 与触发按钮保持适当间距
              p: 0.5, // 内边距，让菜单项有呼吸感
              minWidth: 200,
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              // 企业级柔和阴影
              boxShadow: "0px 8px 24px rgba(149, 157, 165, 0.15)",
              // 防止菜单项圆角被外层遮挡
              "& .MuiMenuItem-root": {
                borderRadius: 1.5,
                mx: 0.5, // 菜单项左右留白
              },
            },
          }}
        >
          <MenuItem
            onClick={handleEditClick}
            sx={{
              py: 1.25,
              px: 1.5,
              fontWeight: 500,
              fontSize: "0.9375rem", // 15px，比标准略大一点，更易读
              display: "flex",
              alignItems: "center",
              gap: 1.5, // 图标与文字的间距
              color: "text.primary",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                color: "primary.main",
                "& svg": { color: "primary.main" },
              },
            }}
          >
            <EditIcon fontSize='small' />
            Edit Company
          </MenuItem>

          {canDeleteCompanies && (
            <>
              <Divider sx={{ my: 0.5, mx: 1 }} />
              <MenuItem
                onClick={() => {
                  setCompanyToDelete(companyToEdit);
                  handleDeleteClick();
                }}
                sx={{
                  py: 1.25,
                  px: 1.5,
                  fontWeight: 500,
                  fontSize: "0.9375rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  color: "error.main",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
                    color: "error.dark",
                    "& svg": { color: "error.dark" },
                  },
                }}
              >
                <DeleteIcon fontSize='small' />
                Delete Company
              </MenuItem>
            </>
          )}
        </Menu>
      )}

      {/* Dialogs - Only render if user has manage access */}
      {canManageCompanies && (
        <>
          <CompanyDialog
            type='edit'
            open={editopen}
            onClose={() => handleDialogClose("edit")}
            subscription={subscription}
            companyData={{
              name: companyName,
              uen,
              financialYearEnd,
              isFullYear,
              isRestricted,
              financialYearStart,
              yearOfAssessment,
              companyType,
            }}
            onConfirm={handleEditConfirm}
            setCompanyName={setCompanyName}
            setUEN={setUEN}
            setFinancialYearEnd={setFinancialYearEnd}
            setIsFullYear={setIsFullYear}
            setIsRestricted={setIsRestricted}
            setFinancialYearStart={setFinancialYearStart}
            setYearOfAssessment={setYearOfAssessment}
            setCompanyType={setCompanyType}
            dialogLoading={dialogLoading}
          />

          <CompanyDialog
            type='delete'
            open={deleteDialogOpen}
            onClose={() => handleDialogClose("delete")}
            companyData={companyToDelete}
            onConfirm={handleDeleteConfirm}
            dialogLoading={dialogLoading}
          />

          <CompanyDialog
            type='create'
            open={open}
            subscription={subscription}
            onClose={() => handleDialogClose("add")}
            companyData={{
              name: companyName,
              uen,
              financialYearEnd,
              isFullYear,
              isRestricted,
              financialYearStart,
              yearOfAssessment,
              companyType,
            }}
            onConfirm={handleAddCompany}
            setCompanyName={setCompanyName}
            setUEN={setUEN}
            setFinancialYearEnd={setFinancialYearEnd}
            setIsFullYear={setIsFullYear}
            setIsRestricted={setIsRestricted}
            setFinancialYearStart={setFinancialYearStart}
            setYearOfAssessment={setYearOfAssessment}
            setCompanyType={setCompanyType}
            dialogLoading={dialogLoading}
          />

          <UploadExcelDialog
            title='Company List'
            open={uploadDialogOpen}
            onClose={closeUploadDialog}
            onDownloadTemplate={downloadCompanyTemplate}
            onFileChange={handleUploadFileChange}
            onUpload={triggerUpload}
            selectedFile={selectedFile}
            uploading={uploading}
            processing={batchProcessing}
            onCloseAlert={closeUploadAlert}
            onReset={handleUploadReset}
            errorFileUrl={errorFileUrl}
            validRows={validRows}
            templateHeadersString={getTemplateHeadersString()}
            templateInstructions={getInstructions(subscription)}
          />
        </>
      )}
    </Box>
  );
};

export default CompanyList;
